import { AxiosError } from 'axios';
import { apiClient } from '../../core/api';
import { env } from '../../core/env';
import type { ShopifyProductCountResponse, ShopifyApiError } from '../../../types/shopify';
import type { Installation, UserProfile } from '../../../types/auth';
import { isWidgetLive } from '../../widgetState';

// Types for Shopify plan selection.
// Price Plan v2 (D3): amount + trial-days are NO LONGER sent by the frontend.
// ShopifyAPI obtains the authoritative price/trial from chatbot-api; the
// storefront must not be trusted on price.
export interface ShopifyPlanSelectionRequest {
  shop: string;
  'selling-plan': string;
  'return-url': string;
  test: boolean;
}

export interface ShopifyPlanSelectionResponse {
  code: number;
  data: {
    appSubscriptionCreate: {
      confirmationUrl: string;
    };
  };
  error?: string;
}

// Mapping for plan names to selling plan format
const PLAN_TO_SELLING_PLAN_MAP: Record<string, { monthly: string; yearly: string }> = {
  basic: { monthly: 'BasicMonthly', yearly: 'BasicYearly' },
  starter: { monthly: 'StarterMonthly', yearly: 'StarterYearly' },
  startup: { monthly: 'ProMonthly', yearly: 'ProYearly' },
  enterprise: { monthly: 'EnterpriseMonthly', yearly: 'EnterpriseYearly' },
  // Price Plan v2
  business: { monthly: 'BusinessMonthly', yearly: 'BusinessYearly' },
  premium: { monthly: 'PremiumMonthly', yearly: 'PremiumYearly' },
  // Digital Staff credit plans (ai_*)
  ai_starter: { monthly: 'AiStarterMonthly', yearly: 'AiStarterYearly' },
  ai_growth: { monthly: 'AiGrowthMonthly', yearly: 'AiGrowthYearly' },
  ai_pro: { monthly: 'AiProMonthly', yearly: 'AiProYearly' },
  ai_scale: { monthly: 'AiScaleMonthly', yearly: 'AiScaleYearly' },
};

// Get selling plan format based on plan name and billing cycle
const getSellingPlanFormat = (planName: string, isYearly: boolean): string => {
  const planKey = planName.toLowerCase();
  const planMapping = PLAN_TO_SELLING_PLAN_MAP[planKey];

  if (!planMapping) {
    throw new Error(`Unknown plan: ${planName}`);
  }

  return isYearly ? planMapping.yearly : planMapping.monthly;
};

export const selectShopifyPlanAndPay = async (
  shopUrl: string,
  planName: string,
  isYearly: boolean,
  userId: string,
  _user: UserProfile,
  paymentStatus: string
): Promise<ShopifyPlanSelectionResponse> => {
  try {
    // Validate inputs
    if (!shopUrl) {
      throw new Error('Shop URL is required');
    }

    if (!planName) {
      throw new Error('Plan name is required');
    }

    // Get selling plan format
    const sellingPlan = getSellingPlanFormat(planName, isYearly);

    // Construct return URL with query parameters — back to the host the merchant is on
    // (dev serves bot-dev and agent-dev), as the Google redirect_uri does.
    const returnUrl = `${window.location.origin}/payment/payment-callback?userId=${encodeURIComponent(userId)}&paymentStatus=${encodeURIComponent(paymentStatus)}`;

    // Prepare request payload. Price Plan v2 (D3): no amount / trial-days —
    // the backend resolves the authoritative quote.
    const payload: ShopifyPlanSelectionRequest = {
      shop: shopUrl,
      'selling-plan': sellingPlan,
      'return-url': returnUrl,
      test: env.shopifyTestPayment,
    };

    console.log('Shopify plan selection request:', {
      shop: shopUrl,
      plan: planName,
      isYearly,
      sellingPlan,
      test: payload.test,
    });

    // Make API call using shopify-specific API method
    const response = await apiClient.shopifyPost<ShopifyPlanSelectionResponse>(
      '/selectPlanAndPay',
      payload
    );

    // Validate response
    if (response.status === 201 && response.data) {
      console.log('Shopify plan selection successful:', response.data);
      return response.data;
    } else {
      throw new Error('Invalid response from Shopify API');
    }
  } catch (error) {
    // Handle API errors with detailed logging
    if (error instanceof AxiosError) {
      const apiError: ShopifyApiError = {
        code: error.response?.status,
        message: error.message,
        error: error.response?.data?.message || 'Unknown API error',
      };

      console.error('Failed to select Shopify plan:', {
        ...apiError,
        shopUrl,
        planName,
        isYearly,
        endpoint: '/selectPlanAndPay',
      });

      throw new Error(`Shopify plan selection failed: ${apiError.error || apiError.message}`);
    } else {
      console.error('Unexpected error during Shopify plan selection:', error);
      throw error;
    }
  }
};

export const hasShopifyInstallation = (user: UserProfile | null): boolean => {
  if (!user?.installations || !Array.isArray(user.installations)) {
    return false;
  }

  return user.installations.some(
    installation => installation.platform === 'shopify' && installation.status !== 'uninstalled'
  );
};

// Utility function to get Shopify shop URL from user data
/** 安装时间:优先 updatedAt,退化到 createdAt,都解析不出来算 0。 */
const installedAt = (i: Installation): number =>
  Date.parse(i.updatedAt || i.createdAt || '') || 0;

/**
 * [3.5 附-2] 取商家**当前**那家 Shopify 店,而不是 installations 数组里的第一条。
 *
 * installations 是历史全量:换店、重装、改域名都会留下多条 shopify 记录,而且旧的那条
 * status 不一定被标成 uninstalled。原来用 `.find()`,谁排在前面就取谁 —— 于是 Home
 * 头部那枚店铺 pill 和 View Store 会一直指着旧域名(验收:显示真实更新后的域名而不是
 * 旧域名)。这里按安装时间取最新的那条。
 *
 * 同一份口径也给 shopifyService.getStoreDomain 用 —— 那条链路负责拼商品 URL
 * (https://{domain}/products/{handle}),域名取错的话生成的商品链接直接是死链。
 */
export const pickCurrentShopifyInstallation = (
  user: UserProfile | null
): Installation | null => {
  if (!user?.installations || !Array.isArray(user.installations)) return null;
  const live = user.installations.filter(
    installation => installation.platform === 'shopify' && installation.status !== 'uninstalled'
  );
  if (live.length <= 1) return live[0] ?? null;
  return live.reduce((newest, i) => (installedAt(i) > installedAt(newest) ? i : newest));
};

export const getShopifyShopUrl = (user: UserProfile | null): string | null =>
  pickCurrentShopifyInstallation(user)?.url || null;

/**
 * 「**Shopify Web Chat 这个渠道**活着吗」——设置页那张卡和它的详情页共用。
 *
 * <p>两处是同一件事的总览与详情（卡片那一行的 Set Up 就链到详情页），各写一份判据
 * 迟早说出两个答案。2026-09-20 线上就出过：同一个商家同一时刻，详情页 Inactive、
 * 卡片 Connected —— 卡片读的是 {@link hasShopifyInstallation}。
 *
 * <p>⚠️ **`hasShopifyInstallation` 回答的是「装没装过」**，不是「挂件开着没有」。
 * 一家把 theme app embed 在主题里关掉的店，它照样说是；而那一行的副标题写着
 * "The chat widget on your storefront"。
 *
 * <p>⚠️ **住在这里而不是 `@lib/widgetState`，是为了复用
 * {@link pickCurrentShopifyInstallation}** —— installations 是历史全量，换店/重装会留下
 * 多条 shopify 行，旧的那条 status 不一定被标成 uninstalled。用 `some()` 的话，一条
 * 没盖过章、status 还停在 active 的旧记录会回退成「在跑」，替当前这家店投出 live，
 * 而详情页上的域名取的是**最新**那条 —— 状态和域名说的就不是同一家店了。
 * `widgetState` 是无依赖的纯函数文件，不该为此反向依赖本模块。
 *
 * <p>⚠️ **这是库里的快照，最多落后巡检 12 小时。** 画给商家看的界面用
 * `useShopifyWidgetLive`（首帧用本函数，再现查一次 Shopify）；本函数留给它当首帧/兜底。
 *
 * <p>`useAnnaChannels` 的 website 磁贴：**有 Shopify 店时**答案就是这一个（经同一个 hook，
 * CS-06 —— 那边原来对所有行 `some()`，一条 `platform=''` 的空壳行就能让它说 connected，
 * 而这里说关着）；**没有 Shopify 店**（自建站）时它才去看 `manual` / 空 platform 行。
 */
export const isShopifyWidgetLive = (user: UserProfile | null): boolean =>
  isWidgetLive(pickCurrentShopifyInstallation(user));

export const fetchShopifyProductCount = async (
  shopUrl: string, 
  activeOnly: boolean = false
): Promise<number | null> => {
  try {
    // Validate shop URL input
    if (!shopUrl) {
      console.warn('Invalid shop URL provided:', shopUrl);
      return null;
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
      shop: shopUrl
    });

    // Add active filter if requested
    if (activeOnly) {
      queryParams.append('query', 'status:active');
    }

    const response = await apiClient.shopifyGet<ShopifyProductCountResponse>(
      `/queryProductsCount?${queryParams.toString()}`
    );

    // Validate response structure and extract product count
    if (response?.data?.code === 200 && response?.data?.data?.productsCount?.count !== undefined) {
      const count = response.data.data.productsCount.count;
      const productType = activeOnly ? 'active' : '';
      console.log(`Shopify ${productType} product count fetched successfully: ${count} ${productType} products for ${shopUrl}`);
      return count;
    } else {
      console.warn('Unexpected response structure from Shopify API:', response?.data);
      return null;
    }
  } catch (error) {
    // Handle API errors with detailed logging
    if (error instanceof AxiosError) {
      const apiError: ShopifyApiError = {
        code: error.response?.status,
        message: error.message,
        error: error.response?.data?.message || 'Unknown API error',
      };

      const productType = activeOnly ? 'active ' : '';
      // Log Shopify-specific error details for debugging
      console.error(`Failed to fetch Shopify ${productType}product count:`, {
        ...apiError,
        shopUrl,
        endpoint: '/queryProductsCount',
        config: {
          url: error.config?.url,
          method: error.config?.method,
        },
      });
    } else {
      const productType = activeOnly ? 'active ' : '';
      console.error(`Unexpected error while fetching ${productType}product count:`, error);
    }

    return null;
  }
};
